import * as PIXI from 'pixi.js';
import { Config } from '../video-data/config';
import { AspectRatio } from '../aspectRatioConfig';
import type { MediaSize } from '../types';
import { TimeAwareMediaAsset } from './asset/TimeAwareMediaAsset';
import { BaseGroup } from './BaseGroup';
import type { BaseAssetSettings } from './asset/BaseAsset';
import type { MediaSourceData } from './asset/MediaAsset';
import { getAttributesToSerialize, NumberAttribute } from './attributes';
import { calculateScaledResolution } from './asset/mediaUtils';
import { resetSprite } from './asset/utils';

export type CompositingAssetSettings = BaseAssetSettings & {
    mediaSizeToUse: MediaSize;
};

/**
 * The CompositingAsset allows for the compositing of two media assets, a background and a foreground into an output
 * texture. This texture can then be used as a source for a media asset through our linkedMediaAsset property.
 */
export class CompositingAsset extends BaseGroup {
    override _classType = 'CompositingAsset';

    #foreground: TimeAwareMediaAsset | null;
    #background: TimeAwareMediaAsset | null;

    @NumberAttribute({ min: 0 })
    accessor startTime = 0;
    #endTime: number | null = null;
    @NumberAttribute({ serialize: true, min: 0, max: 1 })
    accessor brightness = 1;

    #sprite: PIXI.Sprite = new PIXI.Sprite();

    #mediaSizeToUse: MediaSize;

    // TODO(strict-ts): container type should be a union of valid parent types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: CompositingAssetSettings) {
        super(container, settings);

        this.#mediaSizeToUse = settings.mediaSizeToUse;

        const { width, height } = this.getReferenceResolution();
        this.#sprite.width = width;
        this.#sprite.height = height;
        this._addPixiChild(this.#sprite);
    }

    get endTime(): number | null {
        return this.#endTime;
    }

    set endTime(value: number | null) {
        if (this.endTime === value) {
            return;
        }
        this.#endTime = value;
        this.requiresRedraw = true;
    }

    #emitOnLoaded = () => {
        if (this.isLoaded) {
            this.emit('onLoaded');
        }
    };

    get foreground(): TimeAwareMediaAsset | null {
        return this.#foreground ?? null;
    }

    addForeground(id?: string) {
        const settings = {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            timeline: this._globalTimeline,
            autoDraw: this.autoDraw,
            name: 'ForegroundMediaAsset',
            updateParentOnRedraw: true,
            audioMode: this.root.audioMode,
            assetNetworkManager: this._assetNetworkManager,
            getAspectRatio: this._getAspectRatio,
            seekWaitTime: this.root.settings.video.seekWaitTime,
            reusableTextures: this._reusableTextures,
            mediaSizeToUse: this.#mediaSizeToUse,
            getReferenceResolution: () => this.getReferenceResolution(),
            getOutputResolution: () => this.getOutputResolution(),
            ...(id ? { id } : {}),
        };
        this.#foreground = new TimeAwareMediaAsset(this, settings);
        this.insertAssetAt(this.#foreground, 1);
        this.requiresRedraw = true;
        this.#foreground.on('onLoaded', this.#emitOnLoaded);

        return this.#foreground;
    }

    removeForeground() {
        if (!this.foreground) {
            throw new Error('removeForeground called while CompositingAsset has no foreground');
        }
        this.foreground.destroy();
        this.#foreground = null;
    }

    _foregroundCustomLoad(value) {
        const initAutoUpdate = this.autoDraw;
        this.autoDraw = false;
        if (!value) {
            return;
        }
        this.addForeground(value.id);
        if (!this.foreground) {
            throw new Error('Failed to add foreground');
        }
        this.foreground.load(value);
        this.autoDraw = initAutoUpdate;
    }

    _foregroundCustomSave() {
        return this.foreground?.save() ?? null;
    }

    get background(): TimeAwareMediaAsset | null {
        return this.#background ?? null;
    }

    addBackground(id?: string) {
        const settings = {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            timeline: this._globalTimeline,
            autoDraw: this.autoDraw,
            name: 'BackgroundMediaAsset',
            updateParentOnRedraw: true,
            audioMode: this.root.audioMode,
            assetNetworkManager: this._assetNetworkManager,
            getAspectRatio: this._getAspectRatio,
            seekWaitTime: this.root.settings.video.seekWaitTime,
            reusableTextures: this._reusableTextures,
            mediaSizeToUse: this.#mediaSizeToUse,
            getReferenceResolution: () => this.getReferenceResolution(),
            getOutputResolution: () => this.getOutputResolution(),
            ...(id ? { id } : {}),
        };
        this.#background = new TimeAwareMediaAsset(this, settings);
        this.insertAssetAt(this.#background, 0);
        this.requiresRedraw = true;
        this.#background.on('onLoaded', this.#emitOnLoaded);

        return this.#background;
    }

    removeBackground() {
        if (!this.background) {
            throw new Error('removeBackground called while CompositingAsset has no background');
        }
        this.background.destroy();
        this.#background = null;
    }

    _backgroundCustomLoad(value) {
        const initAutoUpdate = this.autoDraw;
        this.autoDraw = false;
        if (!value) {
            return;
        }
        this.addBackground(value.id);
        if (!this.background) {
            throw new Error('Failed to add background');
        }
        this.background.load(value);
        this.autoDraw = initAutoUpdate;
    }

    _backgroundCustomSave() {
        return this.background?.save() ?? null;
    }

    override get isLoaded(): boolean {
        return (
            (!this.background || this.background.isLoaded) &&
            (!this.foreground || this.foreground.isLoaded)
        );
    }

    get isSourceValid(): boolean {
        return (
            (!this.background || this.background.isSourceValid) &&
            (!this.foreground || this.foreground.isSourceValid)
        );
    }

    override get requiresRedraw(): boolean {
        if (!this.getAssets().every((asset) => asset.isLoaded)) {
            return true;
        }
        return super.requiresRedraw;
    }

    override set requiresRedraw(value: boolean) {
        super.requiresRedraw = value;
    }

    override get readonlyProperties() {
        // this asset is part the sceneGroup composition, as such we don't require the serialization of
        // either the classType or the id
        return [];
    }

    override get propertiesToSerialize() {
        return ['foreground', 'background', ...getAttributesToSerialize(this)];
    }

    _syncTrimmingValues() {
        if (!this.foreground) {
            return;
        }
        if (!this.foreground.isVideo) {
            return;
        }
        // The avatar's render URL matches the voiceover's duration so we can sync the timing
        if (this.#mediaSizeToUse === Config.RENDER_MEDIA_SIZE) {
            if (this.foreground._shouldLoad && this.foreground.isLoaded) {
                this.foreground.startTime = this.startTime;
                this.foreground.endTime = this.endTime;
            }
        }
        // The avatar's thumbnail and preview URL does NOT match the voiceover's duration so we don't sync the timing.
        // Since we want to loop the video, we actually need to set the startTime to 0 and endTime to null.
        else {
            this.foreground.startTime = 0;
            this.foreground.endTime = null;
        }
    }

    override _checkAndFixProperties(): void {
        this._syncTrimmingValues();
    }

    override _build() {
        super._build();

        // since we're not part of the scene hierarchy, we need to manually scale the container to match the
        // output resolution
        this.scale.x = this.referenceToOutputMultiplier;
        this.scale.y = this.referenceToOutputMultiplier;

        // scale and position the assets
        if (this.foreground && this.foreground.isLoaded) {
            if (
                this._getAspectRatio() === AspectRatio.VERTICAL ||
                this._getAspectRatio() === AspectRatio.SQUARE
            ) {
                this.foreground.fillMode = Config.FILL_COVER;
            } else {
                this.foreground.fillMode = Config.FILL_CONTAIN;
            }
        }
        if (
            this.background &&
            this.background.isLoaded &&
            this.background.width !== 0 &&
            this.background.height !== 0
        ) {
            this.background.x = 0;
            this.background.y = 0;
        }
    }

    get sprite() {
        return this.#sprite;
    }

    override updateForRender() {
        super.updateForRender();
        if (!this.root.renderer) {
            return;
        }
        if (!this.foreground || !this.background) {
            return;
        }

        this.background.updateForRender();
        const backgroundTextureScale = this.background._sprite.scale;
        const backgroundCanvas = this.background.activeRenderingCanvas;

        this.#sprite.texture = this._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });
        const { width, height } = this.originalResourceResolution;
        this.#sprite.width = width;
        this.#sprite.height = height;

        // PIXI's Resource type doesn't expose `source`, but CanvasResource has it at runtime
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const canvas = (this.#sprite.texture.baseTexture.resource as any)
            .source as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error(
                'Failed to get 2d context; the canvas has already been set to a different context mode',
            );
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.resetTransform();
        ctx.scale(backgroundTextureScale.x, backgroundTextureScale.y);

        ctx.drawImage(backgroundCanvas, 0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = 'source-over';

        const foregroundResource = this.foreground.activeResource;
        if (!foregroundResource) {
            return;
        }
        const scaledMediaTextureResolution = calculateScaledResolution(
            {
                width: foregroundResource.width,
                height: foregroundResource.height,
            },
            {
                width: canvas.width,
                height: canvas.height,
            },
            this._getAspectRatio() === AspectRatio.LANDSCAPE
                ? Config.FILL_CONTAIN
                : Config.FILL_COVER,
        );

        const mediaScale = scaledMediaTextureResolution.width / foregroundResource.width;

        const x = (canvas.width - foregroundResource.width * mediaScale) / 2;

        ctx.resetTransform();

        ctx.scale(mediaScale, mediaScale);

        ctx.drawImage(
            foregroundResource.source as HTMLVideoElement | HTMLImageElement,
            x / mediaScale,
            0,
        );

        this.#sprite.texture.update();
        ctx.resetTransform();
    }

    get activeResource() {
        return this.#sprite.texture.baseTexture.resource as PIXI.CanvasResource;
    }

    /**
     * For now the compositing asset is always the full size of the scene, so we return the resolution
     * of the canvas resolution that will be used to render.
     */
    get originalResourceResolution() {
        return this._reusableTextures.getCanvasResolution();
    }

    /**
     * @deprecated use sourceData.source instead
     */
    get mediaId(): string | null {
        return this.foreground?.mediaId || null;
    }

    /**
     * @deprecated use sourceData.title instead
     */
    get title(): string | null {
        return this.foreground?.sourceData.title || null;
    }

    /**
     * @deprecated use sourceData.source_id instead
     */
    get mediaSourceId(): string | null {
        return this.foreground?.sourceData.source_id || null;
    }

    /**
     * @deprecated use sourceData.license instead
     */
    get license(): string | null {
        return this.foreground?.sourceData.license || null;
    }

    /**
     * @deprecated use sourceData.is_editorial instead
     */
    get isEditorial(): boolean | null {
        return this.foreground?.sourceData.is_editorial || null;
    }

    /**
     * @deprecated use sourceData.source instead
     */
    get mediaSource(): number | null {
        return this.foreground?.sourceData.source || null;
    }

    /**
     * @deprecated use sourceData.type instead
     */
    get mediaAssetType(): string | null {
        return this.foreground?.sourceData.type || null;
    }

    get sourceRenderWidth(): number {
        return this.originalResourceResolution.width;
    }

    get sourceRenderHeight(): number {
        return this.originalResourceResolution.height;
    }

    /**
     * @deprecated use sourceData.render_url instead
     */
    get renderUrl(): string {
        return this.foreground?.sourceData.render_url || '';
    }

    /**
     * @deprecated use sourceData.preview_url instead
     */
    get previewUrl(): string {
        return this.foreground?.sourceData.preview_url || '';
    }

    /**
     * @deprecated use sourceData.poster_url instead
     */
    get posterUrl(): string {
        return this.foreground?.sourceData.poster_url || '';
    }

    /**
     * @deprecated use sourceData.thumbnail_url instead
     */
    get thumbnailUrl(): string {
        return this.foreground?.sourceData.thumbnail_url || '';
    }

    get sourceDuration(): number {
        return this.foreground?.sourceDuration || 0;
    }

    override get hasAudio(): boolean {
        if (super.hasAudio) {
            return true;
        }
        return this.foreground?.sourceData.has_audio || false;
    }

    get hasVoiceover(): boolean {
        return true;
    }

    get isVideo(): boolean {
        return true;
    }

    get volume(): number {
        return this.foreground?.volume || 1;
    }

    get sourceData(): MediaSourceData | null {
        return this.foreground?.sourceData || null;
    }

    get isSourceDataSet(): boolean {
        return this.foreground?.isSourceDataSet || false;
    }

    async onSourceLoaded() {
        await this.foreground?.onSourceLoaded();
        await this.background?.onSourceLoaded();
    }

    generateNewAssetIds() {
        if (this.foreground) {
            this.foreground.generateNewId();
        }
        if (this.background) {
            this.background.generateNewId();
        }
    }

    override get failedToLoad(): boolean {
        return false;
    }

    /**
     * Duration of the asset is the duration of the foreground or the background,
     * whichever is shorter
     */
    override get duration(): number {
        let duration = 0;
        if (this.foreground) {
            duration = this.foreground.duration;
        }
        if (
            this.background &&
            this.background.mediaAssetType === Config.VIDEO_TYPE_BACKGROUND &&
            this.background.duration < duration
        ) {
            duration = this.background.duration;
        }
        return duration;
    }
}
